import './kp-zoomable-image.less';

import type {IAugmentedJQuery, ICompileService, IDocumentService, IOnChanges, IOnDestroy, IOnInit, IPostLink, IScope, ITemplateCacheService, ITimeoutService} from 'angular';
import {ngComponentCssClass} from 'shared/utils/ng-@decorators';
import bind from 'bind-decorator';
import {ResizeObserver} from '@juggle/resize-observer/lib/ResizeObserver';
import type {ResizeObserverEntry} from '@juggle/resize-observer/lib/ResizeObserverEntry';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const fullscreenTemplateUrl = require('./kp-zoomable-image-fullscreen.tpl.pug');

const FULLSCREEN_BODY_CLASS = 'kp-zoomable-image_fullscreen';
const TRANSITION_STYLE_VALUE = 'all 0.2s ease';

export abstract class KpZoomableImageComponentController implements KpZoomableImageComponentBindings, IOnChanges, IOnInit, IPostLink, IOnDestroy {

    private static OVERPOSITION_COEFFICIENT = 1.1;

    public src: string;
    public isFullscreen: boolean;
    public isUltraZoom: boolean;
    public isProtected: boolean;

    public abstract onError(locals: { $event: Event });

    public imageHeight: number;
    public imageWidth: number;
    public mouseMove: boolean;
    public zoomButton: HTMLElement;
    private zoomScope: IScope;
    private timeoutPromise: angular.IPromise<boolean>;
    private fullScreenElement: JQLite;
    private observer: ResizeObserver;

    /*@ngInject*/
    constructor(private $scope: IScope,
                private $document: IDocumentService,
                private $compile: ICompileService,
                private $templateCache: ITemplateCacheService,
                private $timeout: ITimeoutService,
                private $element: IAugmentedJQuery) {
    }

    public $onInit(): void {
        this.observer = new ResizeObserver(this.adjustImageSize);
        if (this.isProtected) {
            this.$element[0].querySelector('.kp-zoomable-image_image').addEventListener('contextmenu', (event) => event.preventDefault());
        }
    }

    public $postLink(): void {
        this.observer.observe(this.element);
        if (this.isFullscreen) {
            this.zoomButton = this.$element[0].parentElement.parentElement.querySelector('.ultra-zoom-button');
        }
    }

    public $onChanges(onChangesObj: angular.IOnChangesObject) {
        if (onChangesObj.src) {
            this.loadImage(onChangesObj.src.currentValue);
        }

        if (onChangesObj.isUltraZoom && onChangesObj.isUltraZoom.currentValue !== undefined) {

            // zoom in
            if (onChangesObj.isUltraZoom.currentValue) {
                this.element.style.backgroundSize = this.computeSmallImageSizeStyle();
                this.element.style.transition = TRANSITION_STYLE_VALUE;
                setTimeout(() => this.element.style.backgroundSize = `${this.imageWidth}px ${this.imageHeight}px`, 0);
                setTimeout(() => this.element.style.transition = '', 200);
            } else {
                this.element.style.backgroundPosition = '';
                this.element.style.transition = TRANSITION_STYLE_VALUE;
                this.element.style.backgroundSize = this.computeSmallImageSizeStyle();
                setTimeout(() => this.element.style.transition = '', 200);
            }
        }
    }

    public $onDestroy(): void {
        this.observer.disconnect();
    }

    public openFullscreen() {
        if (this.isFullscreen) {
            return;
        }

        const fullscreenTemplate = this.$templateCache.get<string>(fullscreenTemplateUrl);
        this.zoomScope = this.$scope.$new();
        this.fullScreenElement = this.$compile(fullscreenTemplate)(this.zoomScope);

        angular.element(this.$document[0].body)
               .addClass(FULLSCREEN_BODY_CLASS)
               .prepend(this.fullScreenElement);

        this.onMouseMoveOnFullscreen();
    }

    public closeFullscreen() {
        this.fullScreenElement.remove();
        angular.element(this.$document[0].body).removeClass(FULLSCREEN_BODY_CLASS);
        this.zoomScope.$destroy();
        this.isUltraZoom = false;
    }

    public onMouseMoveOnFullscreen() {
        this.mouseMove = true;
        this.$timeout.cancel(this.timeoutPromise);

        this.timeoutPromise = this.$timeout(() => this.mouseMove = false, 100);
    }

    public onMouseMoveForUltraZoom($event: JQueryMouseEventObject) {
        if (!this.isUltraZoom) {
            return;
        }

        const centerX = $event.target.getBoundingClientRect().width / 2;
        const centerY = $event.target.getBoundingClientRect().height / 2;

        const deltaX = ($event.offsetX - centerX) * KpZoomableImageComponentController.OVERPOSITION_COEFFICIENT;
        const deltaY = ($event.offsetY - centerY) * KpZoomableImageComponentController.OVERPOSITION_COEFFICIENT;

        const backgroundX = (deltaX / $event.target.getBoundingClientRect().width) * 100 + 50;
        const backgroundY = (deltaY / $event.target.getBoundingClientRect().height) * 100 + 50;

        this.element.style.backgroundPosition = `${backgroundX}% ${backgroundY}%`;
    }

    public setUltraZoom() {
        this.isUltraZoom = true;
    }

    public endUltraZoom() {
        this.isUltraZoom = false;
    }

    @bind
    private adjustImageSize(entries?: ResizeObserverEntry[]) {
        const rect = entries ? entries[0].contentRect : this.element.getBoundingClientRect();

        if (!this.isUltraZoom) {
            if ((rect.height > this.imageHeight && rect.width > this.imageWidth)) {
                this.element.style.backgroundSize = `${this.imageWidth}px ${this.imageHeight}px`;

                // eslint-disable-next-line no-unused-expressions,@typescript-eslint/no-unused-expressions
                this.zoomButton && (this.zoomButton.style.display = 'none');

            } else {
                this.element.style.backgroundSize = 'contain';

                // eslint-disable-next-line no-unused-expressions,@typescript-eslint/no-unused-expressions
                this.zoomButton && (this.zoomButton.style.display = 'block');
            }
        }
    }

    private computeSmallImageSizeStyle(): string {
        const {width: elementWidth, height: elementHeight} = this.element.getBoundingClientRect();

        const imageAspectRatio = this.imageWidth / this.imageHeight;
        const elementAspectRatio = elementWidth / elementHeight;

        // eslint-disable-next-line one-var
        let newWidth, newHeight;

        if (imageAspectRatio <= elementAspectRatio) {
            newWidth = elementHeight * imageAspectRatio;
            newHeight = elementHeight;
        } else {
            newWidth = elementWidth;
            newHeight = elementWidth / imageAspectRatio;
        }

        return `${newWidth}px ${newHeight}px`;
    }

    private loadImage(src: string) {
        const img = new Image();

        img.onerror = (e) => {
            this.onError({$event: e as Event});
        };

        img.onload = () => {
            this.$scope.$apply(() => {
                this.element.style.backgroundImage = `url('${src}')`;
                this.imageHeight = img.height;
                this.imageWidth = img.width;

                this.adjustImageSize();
            });
        };

        img.src = src;
    }

    public get element(): HTMLElement {
        return this.$element[0].querySelector('.kp-zoomable-image_image');
    }
}

/**
 * @ngdoc component
 * @name kpZoomableImage
 * @module portaro.components
 *
 * @param {string} src Image URL
 * @param {function()} [onError] On error callback
 * @param {IHttpResponse<any>} [$event] Error event
 * @param {boolean=} isProtected "Protect" image before downloading.
 *
 *
 * @description
 * Zoomable image component
 */
// eslint-disable-next-line max-classes-per-file
export default class KpZoomableImageComponent {
    public static componentName = 'kpZoomableImage';

    @ngComponentCssClass('kp-zoomable-image')
    public templateUrl = require('./kp-zoomable-image.tpl.pug');

    public bindings = {
        src: '<',
        onError: '&',
        isFullscreen: '@',
        isUltraZoom: '<',
        isProtected: '<?'
    };

    public controller = KpZoomableImageComponentController;
}

interface KpZoomableImageComponentBindings {
    src: string;
    isFullscreen: boolean;
    isUltraZoom: boolean;
    isProtected?: boolean;

    onError(locals: { $event: Event });
}
